import type { MaintenanceTemplate } from '../Pages/Förvaltning/TemplatesEditor/types';

/**
 * An archived template is one that was deleted while plans used it: kept so its
 * activities still resolve (name, interval, cost) everywhere, but no longer
 * offered for new use.
 */
export const isTemplateArchived = (template: Pick<MaintenanceTemplate, 'archivedAt'>): boolean =>
    !!template.archivedAt;

/**
 * Whether a template is offered to a building for new use: in a draft's rows
 * without an entry, the template search and the template lists. Archived ones
 * and those limited to another building are not.
 */
export const templateOfferedTo = (
    template: Pick<MaintenanceTemplate, 'archivedAt' | 'buildingKey'>,
    buildingKey: string | undefined
): boolean =>
    !isTemplateArchived(template) && (!template.buildingKey || template.buildingKey === buildingKey);

/** One `maintenance/active` or `maintenance/drafts` root, as far as the usage check reads it */
type PlansRoot = Record<string, {
    components?: Record<string, { maintenanceActivities?: Record<string, unknown> } | null>;
} | null> | null | undefined;

/** The buildings whose plans (active or draft) hold an activity of the template */
export const buildingsUsingTemplate = (templateId: string, ...roots: PlansRoot[]): string[] => {
    const buildings = new Set<string>();
    roots.forEach(root => {
        Object.entries(root ?? {}).forEach(([buildingKey, plan]) => {
            const used = Object.values(plan?.components ?? {})
                .some(entry => entry?.maintenanceActivities?.[templateId] !== undefined);
            if (used) buildings.add(buildingKey);
        });
    });
    return Array.from(buildings);
};
